/**
 * « Ce que vous pouvez faire maintenant » — la checklist du jour.
 *
 * Chaque ligne est cliquable et mène à l'endroit où l'action se fait vraiment ;
 * les points sont annoncés avant, crédités après, et le total est plafonné —
 * on ne grimpe pas au classement en cliquant, on y grimpe en agissant.
 *
 * Le partage est le seul geste déclaratif (on ne peut pas vérifier un post
 * LinkedIn) : il ouvre le partage ET crédite, une fois par événement.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Share2, Sparkles } from 'lucide-react';
import { appelerApi, envoyer } from '../../reseau.ts';
import type { ActionInfluence, MesActions, MonParrainage } from './types.ts';

/** Où mène chaque action. `partage` est traité à part : il agit sur place. */
const DESTINATIONS: Record<ActionInfluence, { vers?: string; texte: string }> = {
  partage_reseau: { texte: 'Partager' },
  filleul_rejoint: { vers: '/compte/influence#parrainage', texte: 'Mon lien' },
  rdv_planifie: { vers: '/participants', texte: 'Trouver qui voir' },
  rdv_accepte: { vers: '/agenda', texte: 'Mes demandes' },
  rdv_concluant: { vers: '/agenda', texte: 'Confirmer' },
  profil_complete: { vers: '/compte/entreprise', texte: 'Compléter' },
  recommandation_acceptee: { vers: '/participants', texte: 'Recommander' },
};

export function PanneauActions({ evenementId, slug }: { evenementId: string; slug: string }) {
  const [actions, setActions] = useState<MesActions | null>(null);
  const [parrainage, setParrainage] = useState<MonParrainage | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const charger = useCallback(async () => {
    const r = await appelerApi<MesActions>(`/api/influence/actions?evenementId=${encodeURIComponent(evenementId)}`);
    setActions(r.donnees);
  }, [evenementId]);

  useEffect(() => {
    void charger();
    void appelerApi<MonParrainage>('/api/parrainage/moi').then((r) => setParrainage(r.donnees));
  }, [charger]);

  const partager = async () => {
    const lien = parrainage?.page ?? `${location.origin}/evenements/${slug}?p=${parrainage?.code ?? ''}`;
    const texte = `J’y suis ! Retrouvez-moi et prenons rendez-vous : ${lien}`;
    // Le partage natif du téléphone quand il existe, LinkedIn sinon.
    if (navigator.share) {
      await navigator.share({ text: texte, url: lien }).catch(() => undefined);
    } else {
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(lien)}`, '_blank', 'noopener');
    }
    const r = await envoyer<{ points: number; nouveau: boolean; actions: MesActions }>('/api/influence/actions/partage', { evenementId });
    if (r.donnees) {
      setActions(r.donnees.actions);
      setMessage(r.donnees.nouveau ? `+${r.donnees.points} points — merci !` : 'Déjà compté pour cet événement.');
      setTimeout(() => setMessage(null), 4000);
    }
  };

  if (!actions) return null;
  const restantes = actions.actions.filter((a) => !a.fait);

  return (
    <section className="mx-auto mb-5 max-w-[1800px] px-6">
      <div className="rounded-[14px] border border-graphite bg-charbon p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <p className="flex items-center gap-2 text-[16px] font-medium">
              <Sparkles size={15} strokeWidth={1.5} className="text-fumee" />
              Gagnez des points, montez dans le classement
            </p>
            <p className="mt-1 text-[13px] text-fumee">
              {restantes.length > 0
                ? `${restantes.length} action${restantes.length > 1 ? 's' : ''} à votre portée, ici et maintenant.`
                : 'Tout est fait — beau travail. Les points continuent de tomber à chaque rendez-vous conclu.'}
            </p>
          </div>
          <p className="text-right">
            <span className="text-match-gradient font-serif text-[28px] leading-none">+{actions.total}</span>
            <span className="block text-[12px] text-fumee">points d’action / {actions.plafond}</span>
          </p>
        </div>

        {message && <p className="mt-3 text-[13px] text-blanc">{message}</p>}

        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {actions.actions.map((a) => {
            const d = DESTINATIONS[a.cle];
            const contenu = (
              <>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 text-[14px] text-blanc">
                    {a.fait && <Check size={13} strokeWidth={2} className="shrink-0 text-match-rose" />}
                    {a.libelle}
                    {a.fois > 0 && a.maxFois !== 1 && <span className="text-[12px] text-fumee">×{a.fois}</span>}
                  </span>
                  <span className="mt-0.5 block text-[12px] leading-relaxed text-fumee">{a.explication}</span>
                </span>
                <span className="ml-3 shrink-0 text-right">
                  <span className="text-match-gradient block text-[14px]">+{a.points}</span>
                  <span className="mt-0.5 flex items-center justify-end gap-1 text-[11px] text-fumee">
                    {a.cle === 'partage_reseau' ? <Share2 size={10} strokeWidth={1.5} /> : <ArrowRight size={10} strokeWidth={1.5} />}
                    {d.texte}
                  </span>
                </span>
              </>
            );
            const classe = `flex items-start rounded-[10px] border px-3 py-2.5 text-left transition-colors ${a.fait ? 'border-graphite/60 bg-void/40' : 'border-graphite hover:bg-[#262626]'}`;

            if (a.cle === 'partage_reseau') {
              return <button key={a.cle} onClick={() => void partager()} className={classe}>{contenu}</button>;
            }
            if (!d.vers) return <span key={a.cle} className={classe}>{contenu}</span>;
            // Les destinations de l'application d'événement ne sont pas dans le
            // routeur de cette page : lien franc plutôt que navigation interne.
            return d.vers.startsWith('/compte') ? (
              <Link key={a.cle} to={d.vers} className={classe}>{contenu}</Link>
            ) : (
              <a key={a.cle} href={d.vers} className={classe}>{contenu}</a>
            );
          })}
        </div>

        <p className="mt-3 text-[12px] leading-relaxed text-fumee">
          Premium ajoute <span className="text-blanc">+60 points</span> immédiatement et vous place en tête des
          listes —{' '}
          <Link to="/compte/preenregistrement" className="text-blanc underline">voir les formules</Link>.
        </p>
      </div>
    </section>
  );
}
